import React from 'react';
import { Table, Switch, Button, Space, Popconfirm, Tag, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, ExperimentOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useChannelStore } from '../../store/channelStore';
import { NotificationChannel } from '../../types/auth';

/**
 * 渠道表格组件
 * 显示渠道列表并支持状态切换、编辑、删除操作
 */
const ChannelTable: React.FC = () => {
  const {
    channels,
    loading,
    deleteChannel,
    toggleChannelStatus,
    openFormModal,
    openTestModal,
  } = useChannelStore();

  // 获取渠道类型图标和颜色
  const getChannelTypeDisplay = (type: string) => {
    const typeMap: Record<string, { icon: string; color: string; name: string }> = {
      TELEGRAM: { icon: '📱', color: 'blue', name: 'Telegram' },
      DISCORD: { icon: '🎮', color: 'purple', name: 'Discord' },
      DINGTALK: { icon: '💼', color: 'orange', name: '钉钉' },
      FEISHU: { icon: '🚀', color: 'green', name: '飞书' },
      EMAIL: { icon: '📧', color: 'red', name: '邮件' },
      WEBHOOK: { icon: '🔗', color: 'gray', name: 'Webhook' },
      BARK: { icon: '🐕', color: 'cyan', name: 'Bark' },
    };

    const config = typeMap[type] || { icon: '❓', color: 'default', name: type };
    
    return (
      <Tag color={config.color} className="flex items-center">
        <span className="mr-1">{config.icon}</span>
        {config.name}
      </Tag>
    );
  };

  // 处理状态切换
  const handleStatusToggle = async (id: string, checked: boolean) => {
    await toggleChannelStatus(id, checked);
  };

  // 处理编辑
  const handleEdit = (record: NotificationChannel) => {
    openFormModal(record);
  };

  // 处理删除
  const handleDelete = async (id: string) => {
    await deleteChannel(id);
  };

  // 处理测试
  const handleTest = (record: NotificationChannel) => {
    openTestModal(record);
  };

  // 表格列定义
  const columns: ColumnsType<NotificationChannel> = [
    {
      title: '通知名称',
      dataIndex: 'channel_name',
      key: 'channel_name',
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text}>
          <span className="font-medium">{text}</span>
        </Tooltip>
      ),
    },
    {
      title: '渠道类型',
      dataIndex: 'channel_type',
      key: 'channel_type',
      width: 120,
      render: (type: string) => getChannelTypeDisplay(type),
    },
    {
      title: '状态',
      dataIndex: 'is_enabled',
      key: 'is_enabled',
      width: 80,
      render: (enabled: boolean, record: NotificationChannel) => (
        <Switch
          checked={enabled}
          onChange={(checked) => handleStatusToggle(record.id, checked)}
          checkedChildren="启用"
          unCheckedChildren="禁用"
          loading={loading}
        />
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => {
        const formattedDate = new Date(date).toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
        return <span className="text-gray-600">{formattedDate}</span>;
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_, record: NotificationChannel) => (
        <Space size="small">
          <Tooltip title="测试渠道">
            <Button
              type="link"
              icon={<ExperimentOutlined />}
              onClick={() => handleTest(record)}
              className="text-blue-600 hover:text-blue-800"
            >
              测试
            </Button>
          </Tooltip>
          <Tooltip title="编辑渠道">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              className="text-green-600 hover:text-green-800"
            >
              编辑
            </Button>
          </Tooltip>
          <Tooltip title="删除渠道">
            <Popconfirm
              title="确认删除"
              description="确定要删除这个通知渠道吗？此操作不可恢复。"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
              okType="danger"
            >
              <Button
                type="link"
                icon={<DeleteOutlined />}
                className="text-red-600 hover:text-red-800"
                danger
              >
                删除
              </Button>
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Table<NotificationChannel>
      columns={columns}
      dataSource={channels}
      rowKey="id"
      loading={loading}
      pagination={{
        total: channels.length,
        pageSize: 10,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) =>
          `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
      }}
      locale={{
        emptyText: (
          <div className="py-8 text-center">
            <div className="text-4xl mb-4">📢</div>
            <div className="text-gray-500 mb-2">暂无通知渠道</div>
            <div className="text-gray-400 text-sm">
              点击右上角"新建渠道"按钮创建您的第一个通知渠道
            </div>
          </div>
        ),
      }}
      scroll={{ x: 800 }}
      className="channel-table"
    />
  );
};

export default ChannelTable;
